/**
 * PushSkuRulesSection: the body of the Skip or Always 0 card on Stock Sync >
 * Configuration.
 *
 * Two rules, one list:
 *  - Skip: the automatic push leaves the SKU alone, so Shopify keeps its number.
 *  - Always 0: the push always writes 0, whatever stock PrintRun holds. A base
 *    garment reaches every design printed on it.
 *
 * APPLIES IMMEDIATELY, like `ErpStockAuthoritySection` beside it: every change
 * is one `setPushSkuRules` call, outside the host form's dirty model and its
 * "Unsaved changes" bar (the card header says so). The host is a `<form>`, so
 * every control here is `type="button"` and Enter in a text box never submits.
 *
 * This parent is thin: it holds the dialogs and the one way a change is made.
 * `PushRuleSearchPanel` adds, `PushRulesList` lists and changes. Each hands a
 * change here; the answer is a promise, so the list clears its selection only
 * once a change has landed (after a confirm, if one was asked):
 *  - Skip goes straight through.
 *  - Always 0 always asks first (`AlwaysZeroConfirmDialog`), numbers first.
 *  - Remove asks first when a live Always 0 row is in it (a retired SKU's or a
 *    pre-printed design's rule is live: the push still applies it)
 *    (`RemoveRulesConfirmDialog`), else goes straight through. Rules that do
 *    nothing are removed without one.
 * Every change's toast offers Undo (`useSetPushSkuRules`).
 *
 * Focus after a change: a search row's button is now disabled ("Skipped"), so
 * focus goes back to the search box for the next SKU; a change from the list
 * lands on the list heading.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Eye, PauseCircle, ShieldAlert } from 'lucide-react';
import { NoteLine } from '@/components/shared/NoteLine';
import { trpc } from '@/lib/trpc';
import { useSetPushSkuRules } from '../hooks/useSetPushSkuRules';
import {
  groupPreviewInput,
  groupTargetKey,
  idsToChange,
  isInertStatus,
  summarizeAlwaysZero,
  willPauseThePush,
  type AlwaysZeroSummary,
  type GroupTarget,
  type PushRuleRow,
  type PushRunState,
  type PushSkuRule,
  type PushSkuRuleOrNone,
} from '../utils/push-sku-rules';
import { AlwaysZeroConfirmDialog } from './AlwaysZeroConfirmDialog';
import { PasteSkuListDialog } from './PasteSkuListDialog';
import { RuleBadge } from './PushRuleBadge';
import { PushRuleSearchPanel, type SearchPending } from './PushRuleSearchPanel';
import { PushRulesList, type ListPending } from './PushRulesList';
import { RemoveRulesConfirmDialog } from './RemoveRulesConfirmDialog';

const EMPTY_RULES: PushRuleRow[] = [];

type Origin = 'search' | 'list';

interface InFlight {
  origin: Origin;
  /** `sku:<id>` / `product:<id>` / `garment:<code>` from the search, `bulk` / `inert` from the list. */
  key: string;
  rule: PushSkuRuleOrNone;
}

type ZeroTarget = { kind: 'skus'; skuIds: string[] } | { kind: 'group'; group: GroupTarget };

interface ZeroConfirm {
  origin: Origin;
  target: ZeroTarget;
  summary: AlwaysZeroSummary | null;
  /** The ids that will actually change, once the preview has landed. */
  changeIds: string[] | null;
  willPause: boolean;
  previewFailed: boolean;
}

interface RemoveConfirm {
  origin: Origin;
  skuIds: string[];
  baseGarmentCount: number;
}

interface PushSkuRulesSectionProps {
  /** Where the live push stands, read from the SAVED settings. */
  runState: PushRunState;
  /** The SAVED Always 0 limit (0 = No limit): the big-batch pause reads its ceiling off it. */
  alwaysZeroLimit: number;
  /** The host form's save bar is showing: lift the selection pill above it. */
  raiseSelectionBar?: boolean;
}

export function PushSkuRulesSection({ runState, alwaysZeroLimit, raiseSelectionBar = false }: PushSkuRulesSectionProps) {
  const { t } = useTranslation('inventory');
  const rulesQuery = trpc.stockSync.listPushSkuRules.useQuery(undefined, { staleTime: 30_000 });
  const { apply, isPending: applying } = useSetPushSkuRules();
  const previewMutation = trpc.stockSync.previewPushSkuRules.useMutation();
  const preview = previewMutation.mutateAsync;

  const rules = rulesQuery.data?.rules ?? EMPTY_RULES;
  const ruleBySkuId = useMemo(() => new Map(rules.map((row) => [row.skuId, row.rule])), [rules]);
  // Read inside stable callbacks, so the memoised panels do not re-render on every change.
  const rulesRef = useRef(rules);
  rulesRef.current = rules;
  const limitRef = useRef(alwaysZeroLimit);
  limitRef.current = alwaysZeroLimit;

  const searchInputRef = useRef<HTMLInputElement>(null);
  const listHeadingRef = useRef<HTMLHeadingElement>(null);
  const focusAfter = useCallback((origin: Origin) => {
    (origin === 'search' ? searchInputRef.current : listHeadingRef.current)?.focus({ preventScroll: true });
  }, []);

  const [inFlight, setInFlight] = useState<InFlight | null>(null);
  const [zeroConfirm, setZeroConfirm] = useState<ZeroConfirm | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState<RemoveConfirm | null>(null);
  const [paste, setPaste] = useState({ open: false, session: 0 });

  // One confirm at a time; its answer settles the promise the caller awaits.
  const settleRef = useRef<((ok: boolean) => void) | null>(null);
  const ask = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        settleRef.current?.(false);
        settleRef.current = resolve;
      }),
    [],
  );
  const settle = (ok: boolean) => {
    const resolve = settleRef.current;
    settleRef.current = null;
    resolve?.(ok);
  };
  // Where focus goes when a confirm closes on a change that landed.
  const landedRef = useRef<Origin | null>(null);
  const onConfirmCloseFocus = (event: Event) => {
    // Cancel returns focus to the button that opened it (Radix default);
    // after a change lands, that button is gone or disabled.
    const landed = landedRef.current;
    if (!landed) return;
    landedRef.current = null;
    event.preventDefault();
    focusAfter(landed);
  };

  // ── Always 0 ──────────────────────────────────────────────────────────────
  const zeroRequest = useRef(0);
  const startAlwaysZero = useCallback(
    (target: ZeroTarget, origin: Origin) => {
      const request = ++zeroRequest.current;
      setZeroConfirm({ origin, target, summary: null, changeIds: null, willPause: false, previewFailed: false });
      preview(target.kind === 'skus' ? { skuIds: target.skuIds } : groupPreviewInput(target.group))
        .then((result) => {
          if (request !== zeroRequest.current) return;
          setZeroConfirm(
            (prev) =>
              prev && {
                ...prev,
                summary: summarizeAlwaysZero(result.matched),
                changeIds: idsToChange(result.matched, 'always_zero'),
                willPause: willPauseThePush(result, limitRef.current),
              },
          );
        })
        .catch(() => {
          if (request !== zeroRequest.current) return;
          setZeroConfirm((prev) => prev && { ...prev, previewFailed: true });
        });
    },
    [preview],
  );

  const confirmAlwaysZero = async () => {
    if (!zeroConfirm) return;
    const { target, changeIds, origin } = zeroConfirm;
    const ids = changeIds ?? (target.kind === 'skus' ? target.skuIds : []);
    const ok = await apply(ids, 'always_zero');
    if (!ok) return;
    landedRef.current = origin;
    setZeroConfirm(null);
    settle(true);
  };
  const cancelAlwaysZero = () => {
    zeroRequest.current += 1;
    setZeroConfirm(null);
    settle(false);
  };

  // ── Remove (asks only when a live Always 0 row is in it) ──────────────────
  const confirmRemove = async () => {
    if (!removeConfirm) return;
    const ok = await apply(removeConfirm.skuIds, 'none');
    if (!ok) return;
    landedRef.current = removeConfirm.origin;
    setRemoveConfirm(null);
    settle(true);
  };
  const cancelRemove = () => {
    setRemoveConfirm(null);
    settle(false);
  };

  // ── The one way a change is made ──────────────────────────────────────────
  const direct = useCallback(
    async (skuIds: string[], rule: PushSkuRuleOrNone, origin: Origin, key: string) => {
      setInFlight({ origin, key, rule });
      const ok = await apply(skuIds, rule);
      setInFlight(null);
      if (ok) focusAfter(origin);
      return ok;
    },
    [apply, focusAfter],
  );

  const request = useCallback(
    (skuIds: string[], rule: PushSkuRuleOrNone, origin: Origin, key: string): Promise<boolean> => {
      if (skuIds.length === 0) return Promise.resolve(false);
      if (rule === 'always_zero') {
        startAlwaysZero({ kind: 'skus', skuIds }, origin);
        return ask();
      }
      if (rule === 'none') {
        const byId = new Map(rulesRef.current.map((row) => [row.skuId, row]));
        // A rule that does nothing brings no stock back, so it never asks. A
        // retired SKU's or a pre-printed design's rule is live (the push still
        // applies it), so it does.
        const liveZero = skuIds
          .map((id) => byId.get(id))
          .filter((row): row is PushRuleRow => row?.rule === 'always_zero' && !isInertStatus(row.status));
        if (liveZero.length > 0) {
          setRemoveConfirm({
            origin,
            skuIds,
            baseGarmentCount: liveZero.filter((row) => row.isBaseGarment).length,
          });
          return ask();
        }
      }
      return direct(skuIds, rule, origin, key);
    },
    [ask, direct, startAlwaysZero],
  );

  const onSku = useCallback(
    (skuId: string, rule: PushSkuRule) => void request([skuId], rule, 'search', `sku:${skuId}`),
    [request],
  );

  // A whole product, or a whole 3PL garment (a client's copies of it have no
  // product): both find their SKUs on the server by the group's identity.
  const onGroup = useCallback(
    async (group: GroupTarget, rule: PushSkuRule) => {
      if (rule === 'always_zero') {
        startAlwaysZero({ kind: 'group', group }, 'search');
        return;
      }
      // Skip a whole group: find its SKUs, then set the ones not skipped yet.
      // Skip can never zero a storefront, so the Shopify impact is not read.
      const key = groupTargetKey(group);
      setInFlight({ origin: 'search', key, rule });
      let ids: string[];
      let anyMatched: boolean;
      try {
        const result = await preview({ ...groupPreviewInput(group), withImpact: false });
        ids = idsToChange(result.matched, 'skip');
        anyMatched = result.matched.length > 0;
      } catch {
        setInFlight(null);
        toast.error(t('stockSync.pushRules.product.findFailed'), { duration: Infinity });
        return;
      }
      if (ids.length === 0) {
        setInFlight(null);
        // No SKU at all (every one a pre-printed design, say) is not "already set".
        toast.success(
          anyMatched ? t('stockSync.pushRules.toast.nothingChanged') : t('stockSync.pushRules.product.noneCanTake'),
        );
        return;
      }
      await direct(ids, 'skip', 'search', key);
    },
    [direct, preview, startAlwaysZero, t],
  );

  const onListChange = useCallback(
    (skuIds: string[], rule: PushSkuRuleOrNone) => request(skuIds, rule, 'list', 'bulk'),
    [request],
  );
  const onRemoveInert = useCallback(
    (skuIds: string[]) => void direct(skuIds, 'none', 'list', 'inert'),
    [direct],
  );
  const openPaste = useCallback(() => setPaste((prev) => ({ open: true, session: prev.session + 1 })), []);
  const onPasteOpenChange = useCallback((open: boolean) => setPaste((prev) => ({ ...prev, open })), []);
  const refetchRules = rulesQuery.refetch;
  const retryList = useCallback(() => void refetchRules(), [refetchRules]);

  const busy =
    applying || inFlight !== null || zeroConfirm !== null || removeConfirm !== null || previewMutation.isPending;
  const searchPending: SearchPending | null =
    inFlight?.origin === 'search' && inFlight.rule !== 'none' ? { key: inFlight.key, rule: inFlight.rule } : null;
  const listPending: ListPending | null =
    inFlight?.origin === 'list' ? (inFlight.key === 'inert' ? 'inert' : inFlight.rule) : null;

  const runNote =
    runState === 'halted'
      ? { icon: ShieldAlert, text: t('stockSync.pushRules.runState.halted') }
      : runState === 'off'
        ? { icon: PauseCircle, text: t('stockSync.pushRules.runState.off') }
        : runState === 'dry_run'
          ? { icon: Eye, text: t('stockSync.pushRules.runState.dryRun') }
          : null;

  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-2 gap-y-1.5 text-xs">
        <dt>
          <RuleBadge rule="skip" />
        </dt>
        <dd className="max-w-prose text-foreground">{t('stockSync.pushRules.rule.skipMeaningLong')}</dd>
        <dt>
          <RuleBadge rule="always_zero" />
        </dt>
        <dd className="max-w-prose text-foreground">{t('stockSync.pushRules.rule.alwaysZeroMeaningLong')}</dd>
      </dl>

      {runNote && (
        <NoteLine icon={runNote.icon} tone="warning" as="p" className="text-sm">
          {runNote.text}
        </NoteLine>
      )}

      <PushRuleSearchPanel
        ruleBySkuId={ruleBySkuId}
        busy={busy}
        pending={searchPending}
        onSku={onSku}
        onGroup={onGroup}
        onPaste={openPaste}
        inputRef={searchInputRef}
      />

      <PushRulesList
        rules={rules}
        state={rulesQuery.isError ? 'error' : rulesQuery.isLoading ? 'loading' : 'ready'}
        retrying={rulesQuery.isFetching}
        onRetry={retryList}
        busy={busy}
        pending={listPending}
        onChangeRules={onListChange}
        onRemoveInert={onRemoveInert}
        onPaste={openPaste}
        headingRef={listHeadingRef}
        raiseSelectionBar={raiseSelectionBar}
      />

      <AlwaysZeroConfirmDialog
        open={zeroConfirm !== null}
        summary={zeroConfirm?.summary ?? null}
        previewFailed={zeroConfirm?.previewFailed ?? false}
        willPause={zeroConfirm?.willPause ?? false}
        requestedCount={zeroConfirm?.target.kind === 'skus' ? zeroConfirm.target.skuIds.length : 0}
        loadingLabel={
          zeroConfirm?.target.kind === 'group' ? t('stockSync.pushRules.product.finding') : undefined
        }
        applying={applying}
        onConfirm={() => void confirmAlwaysZero()}
        onCancel={cancelAlwaysZero}
        onCloseAutoFocus={onConfirmCloseFocus}
      />

      <RemoveRulesConfirmDialog
        open={removeConfirm !== null}
        count={removeConfirm?.skuIds.length ?? 0}
        baseGarmentCount={removeConfirm?.baseGarmentCount ?? 0}
        applying={applying}
        onConfirm={() => void confirmRemove()}
        onCancel={cancelRemove}
        onCloseAutoFocus={onConfirmCloseFocus}
      />

      <PasteSkuListDialog
        open={paste.open}
        session={paste.session}
        alwaysZeroLimit={alwaysZeroLimit}
        onOpenChange={onPasteOpenChange}
      />
    </div>
  );
}
